import React, { useEffect } from 'react';
import { Provider as ChakraProvider } from '@/components/ui/provider';
import { ApolloClient, createHttpLink, InMemoryCache, ApolloLink } from '@apollo/client';
import { ApolloProvider } from '@apollo/client/react';
import { DefaultSeo } from 'next-seo';
import { setContext } from '@apollo/client/link/context'; // Removed GraphQL WebSocket dependencies - using Amplify native subscriptions
import { onError } from '@apollo/client/link/error';
import { Amplify } from 'aws-amplify';
import { fetchAuthSession, signOut as authSignOut } from 'aws-amplify/auth';
import '@/styles/main.scss';
import { applyStoredLanguage } from '@/libs/i18n';
import { useTranslation } from 'react-i18next';
import { AuthProvider } from '@/contexts/AuthContext';
import { AnalyticsProvider } from '@/contexts/AnalyticsContext';
import { OrganisationProvider } from '@/contexts/OrganisationContext';
import { PirschAnalyticsProvider } from '@/contexts/PirschAnalyticsContext';
import { BugsnagProvider } from '@/contexts/BugsnagContext';
import CookieConsent from '@/components/CookieConsent';
import { EventProvider } from '@/contexts/EventContext';
import { Toaster } from '@/components/ui/toaster';

interface AppProps {
  Component: any;
  pageProps: any;
}

/* AWS Amplify and AppSync - Simplified setup for queries/mutations only */
const APPSYNC_URL = process.env.NEXT_PUBLIC_APP_SYNC_API_URL;

const httpLink = createHttpLink({
  uri: APPSYNC_URL
});

const authLink = setContext(async (_, { headers }) => {
  try {
    const session = await fetchAuthSession();
    const token = session.tokens?.idToken?.toString();
    return {
      headers: {
        ...headers,
        authorization: token
      }
    };
  } catch (err) {
    console.error('Auth session token error', err);

    // The Cognito session/identity has been invalidated (revoked, expired, or
    // signed out elsewhere) — force a local sign-out so AuthContext's Hub
    // listener clears auth state and the UI redirects, instead of silently
    // retrying every request with no token.
    authSignOut().catch((signOutErr) => console.error('Auth session forced sign-out error', signOutErr));

    return { headers: { ...headers } };
  }
});

const errorLink = onError(({ graphQLErrors, networkError, operation }) => {
  if (graphQLErrors) {
    graphQLErrors.forEach((error) => {
      const isUnknownField =
        error.message.toLowerCase().includes('unknown field') ||
        error.message.toLowerCase().includes('cannot query field') ||
        error.extensions?.code === 'GRAPHQL_VALIDATION_FAILED';

      if (isUnknownField) {
        const err = new Error(
          `[GraphQL field mismatch] Query "${operation.operationName}" requests a field that does not exist in the schema: ${error.message}`
        );
        console.error(err.message);
      } else {
        console.error(`[GraphQL error] Operation: ${operation.operationName}`, error);
      }
    });
  }
  if (networkError) {
    console.error(`[Network error] Operation: ${operation.operationName}`, networkError);
  }
});

// Apollo Client for queries/mutations only - subscriptions handled by Amplify
const client = new ApolloClient({
  link: ApolloLink.from([errorLink, authLink, httpLink]),
  cache: new InMemoryCache({
    typePolicies: {
      Query: {
        fields: {
          invoiceList: { merge: false },
          creditNoteList: { merge: false },
          userStatusList: {
            keyArgs: ['input', ['orgId']],
            merge: false
          }
        }
      },
      UserListItem: {
        keyFields: ['id'],
        fields: {
          _optimistic: { read: (v) => v ?? null }
        }
      },
      UserInviteItem: {
        keyFields: ['id'],
        fields: {
          _optimistic: { read: (v) => v ?? null }
        }
      }
    }
  })
});

// Copied and variable-ised settings from src/aws-exports.js. Check for changes when changing amplify
const config = {
  aws_project_region: process.env.NEXT_PUBLIC_REGION,
  aws_cognito_identity_pool_id: process.env.NEXT_PUBLIC_IDENTITY_POOL_ID,
  aws_cognito_region: process.env.NEXT_PUBLIC_REGION,
  aws_user_pools_id: process.env.NEXT_PUBLIC_USER_POOL_ID,
  aws_user_pools_web_client_id: process.env.NEXT_PUBLIC_USER_POOL_WEB_CLIENT_ID,
  oauth: {
    domain: process.env.NEXT_PUBLIC_OAUTH_DOMAIN,
    scope: ['email', 'openid', 'profile', 'aws.cognito.signin.user.admin'],
    redirectSignIn: process.env.NEXT_PUBLIC_OAUTH_REDIRECT_SIGN_IN,
    redirectSignOut: process.env.NEXT_PUBLIC_OAUTH_REDIRECT_SIGN_OUT,
    responseType: 'code',
    options: {
      AdvancedSecurityDataCollectionFlag: false
    }
  },
  aws_cognito_username_attributes: ['EMAIL'],
  aws_cognito_social_providers: [],
  aws_cognito_signup_attributes: ['EMAIL'],
  aws_cognito_mfa_configuration: 'OPTIONAL',
  aws_cognito_mfa_types: ['TOTP'],
  aws_cognito_password_protection_settings: {
    passwordPolicyMinLength: 8,
    passwordPolicyCharacters: []
  },
  aws_cognito_verification_mechanisms: ['EMAIL'],
  aws_user_files_s3_bucket: process.env.NEXT_PUBLIC_S3_BUCKET,
  aws_user_files_s3_bucket_region: process.env.NEXT_PUBLIC_REGION,
  // Add AppSync API configuration for Amplify subscriptions
  aws_appsync_graphqlEndpoint: process.env.NEXT_PUBLIC_APP_SYNC_API_URL,
  aws_appsync_region: process.env.NEXT_PUBLIC_REGION,
  aws_appsync_authenticationType: 'AMAZON_COGNITO_USER_POOLS'
};

Amplify.configure(config);

const LanguageBootstrap: React.FC = () => {
  const { i18n } = useTranslation();

  useEffect(() => {
    applyStoredLanguage();
  }, [i18n]);

  useEffect(() => {
    document.documentElement.lang = i18n.language?.substring(0, 2) || 'en';
  }, [i18n.language]);

  return null;
};

const App: React.FC<AppProps> = ({ Component, pageProps }) => (
  <BugsnagProvider>
    <ChakraProvider>
      <DefaultSeo
        // dangerouslyDisableGooglebot={process.env.NEXT_PUBLIC_STAGE !== 'prod'}
        dangerouslySetAllPagesToNoFollow={process.env.NEXT_PUBLIC_STAGE !== 'prod'}
        dangerouslySetAllPagesToNoIndex={process.env.NEXT_PUBLIC_STAGE !== 'prod'}
        openGraph={{
          type: 'website',
          locale: 'en_GB',
          url: 'https://eezi.io/',
          site_name: 'eezi',
          images: [
            {
              url: 'https://eezi.io/images/open-graph-rect.png',
              width: 1200,
              height: 630,
              alt: 'eezi e-invoicing - Powered by VAT IT'
            },
            {
              url: 'https://eezi.io/images/open-graph-square.png',
              width: 1200,
              height: 1200,
              alt: 'eezi e-invoicing - Powered by VAT IT'
            }
          ]
        }}
        twitter={{
          cardType: 'summary_large_image'
        }}
      />

      <ApolloProvider client={client as any}>
        <PirschAnalyticsProvider>
          <AnalyticsProvider>
            <AuthProvider>
              <OrganisationProvider>
                <EventProvider>
                  <LanguageBootstrap />
                  <Component {...pageProps} />
                  <CookieConsent />
                  <Toaster />
                </EventProvider>
              </OrganisationProvider>
            </AuthProvider>
          </AnalyticsProvider>
        </PirschAnalyticsProvider>
      </ApolloProvider>
    </ChakraProvider>
  </BugsnagProvider>
);

export default App;
